import { createModel } from '@rematch/core';
import type { RootModel } from './models';
import nonCriticalException from '../modules/exceptionLogger';
import {
  getDeviceLocation,
  checkLocationPermissionsGranted,
  DeviceLocation,
} from '../modules/consumer/DeviceLocation';
import {
  getItem,
  setItem,
  removeItem,
} from '../modules/KeyValueStorage';

type LocationState = {
  latitude?: number;
  longitude?: number;
  address?: string;
  created?: number;
  loading: boolean;
};

export type LocationRecord = Pick<LocationState, 'address' | 'latitude' | 'longitude' | 'created'> & {
  placeId?: string;
};

/** Cached for a week */
export const GEO_LOCATION_CACHE_MAX_AGE: number = 7 /* days */
  * 24 /* hours */
  * 60 /* minutes */
  * 60 /* seconds */
  * 1000; /* milliseconds */

interface SetLocationPayload {
  latitude: number;
  longitude: number;
  address: string;
  created: number;
}

/**
 * The location model is used to load and cache a user's Geolocation
 * via the browser's geolocation API (when available).
 * It initializes when the store is, using the latest cache from localforage
 * if it exists, otherwise loading the current geolocation.
 *
 * Consumers may handle max age/expiration separately by reading the
 * `created` timestamp attribute
 */
const model = createModel<RootModel>()({
  name: 'location',

  state: {
    loading: false,
  } as LocationState,

  reducers: {
    setLocation: (state: LocationState, payload: SetLocationPayload) => ({
      ...state,
      ...payload,
    }),
    setLoading: (state: LocationState, loading: boolean) => ({
      ...state,
      loading,
    }),
  },

  effects: dispatch => ({
    // allows other effects to return the location model state after reducers have been applied
    geoLocationLoaded: async (
      payload: undefined,
      rootState,
    ): Promise<LocationState> => rootState.location,
    loadGeolocation: async (): Promise<LocationState | null> => {
      dispatch.location.setLoading(true);
      try {
        const {
          latitude,
          longitude,
          addressComponents: {
            city,
            state,
          } = {},
        }: DeviceLocation = await getDeviceLocation();
        const location: SetLocationPayload = {
          latitude: Number(latitude),
          longitude: Number(longitude),
          address: [
            city,
            state,
          ].filter(i => !!i).join(', '),
          created: Date.now(),
        };
        setItem('geolocation', location);
        await dispatch.location.setLocation(location);

        // return the location model state after the reducer has been applied
        return await dispatch.location.geoLocationLoaded();
      } catch (e) {
        nonCriticalException(e);
      } finally {
        dispatch.location.setLoading(false);
      }
      return null;
    },
    initialize: async (): Promise<LocationState | null> => {
      // Check the cache, initialize from that
      const location = await getItem('geolocation') as SetLocationPayload;
      if (location
        && location?.created + GEO_LOCATION_CACHE_MAX_AGE > Date.now()) {
        await dispatch.location.setLocation(location);
        return { ...location, loading: false };
      }
      // Cache is nonexistant or old, so remove anything that's there
      removeItem('geolocation');

      // Check geolocation perms and initialize geolocation
      const hasPermission = await checkLocationPermissionsGranted();
      if (hasPermission) {
        return dispatch.location.loadGeolocation();
      }
      return null;
    },
  }),
});

export default model;
